import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ADMIN_SESSION_KEY, readStoredAdminSession } from '../admin/adminUtils';

const AuthContext = createContext(null);

async function authApiPost(token, payload) {
  const t = String(token || '').trim();
  const r = await fetch('/api/admin_auth.php', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Session': t,
      Authorization: `Bearer ${t}`,
    },
    body: JSON.stringify({ session_token: t, ...payload }),
  });
  let d = {};
  try {
    d = await r.json();
  } catch {
    return { ok: false, error: 'Invalid response from server.', status: r.status };
  }
  if (!r.ok || !d.success) {
    return { ok: false, error: d.error || 'Request failed.', status: r.status, data: d };
  }
  return { ok: true, data: d, status: r.status };
}

function profileFromPayload(d) {
  return {
    displayName: d?.display_name ? String(d.display_name) : '',
    email: d?.email ? String(d.email) : '',
    phone: d?.phone ? String(d.phone) : '',
  };
}

function authedState(token, d) {
  const role = typeof d?.role === 'string' && d.role ? d.role : 'member';
  const profile = profileFromPayload(d);
  return {
    status: 'authed',
    token,
    username: String(d?.username || ''),
    role,
    displayName: profile.displayName,
    email: profile.email,
    phone: profile.phone,
  };
}

const GUEST_STATE = {
  status: 'guest',
  token: '',
  username: '',
  role: '',
  displayName: '',
  email: '',
  phone: '',
};

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    status: 'loading',
    token: '',
    username: '',
    role: '',
    displayName: '',
    email: '',
    phone: '',
  });

  const applyGuest = useCallback(() => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    localStorage.removeItem(ADMIN_SESSION_KEY);
    setState(GUEST_STATE);
  }, []);

  const refreshAuth = useCallback(async () => {
    const t = String(readStoredAdminSession() || '').trim();
    if (!t) {
      setState(GUEST_STATE);
      return;
    }
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 12000);
    try {
      const r = await fetch('/api/admin_auth.php', {
        headers: {
          'X-Admin-Session': t,
          Authorization: `Bearer ${t}`,
        },
        signal: controller.signal,
      });
      let d = {};
      try {
        d = await r.json();
      } catch {
        applyGuest();
        return;
      }
      if (!r.ok || !d.logged_in) {
        applyGuest();
        return;
      }
      setState(authedState(t, d));
    } catch {
      applyGuest();
    } finally {
      window.clearTimeout(timeoutId);
    }
  }, [applyGuest]);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const login = useCallback(async (username, password, remember) => {
    const u = String(username || '').trim();
    const p = String(password || '');
    if (!u || !p) return { ok: false, error: 'Enter username or email and password.' };
    let r;
    try {
      r = await fetch('/api/admin_auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', username: u, password: p }),
      });
    } catch {
      return {
        ok: false,
        error:
          'Could not reach the sign-in service. If you are developing locally, run npm run php:serve in another terminal.',
      };
    }
    let d = {};
    try {
      d = await r.json();
    } catch {
      return {
        ok: false,
        error:
          'Invalid response from server. Ensure PHP is running (npm run php:serve) so /api/admin_auth.php returns JSON.',
      };
    }
    if (!r.ok || !d.success) return { ok: false, error: d.error || 'Sign-in failed.' };
    const tok = String(d.session_token || '').trim();
    if (!tok) return { ok: false, error: 'No session returned.' };
    sessionStorage.setItem(ADMIN_SESSION_KEY, tok);
    if (remember) localStorage.setItem(ADMIN_SESSION_KEY, tok);
    else localStorage.removeItem(ADMIN_SESSION_KEY);
    const next = authedState(tok, { ...d, username: d.username || u });
    setState(next);
    return { ok: true, role: next.role, username: next.username };
  }, []);

  const register = useCallback(async (fields) => {
    const r = await fetch('/api/admin_auth.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'register',
        username: String(fields?.username || ''),
        password: String(fields?.password || ''),
        confirm_password: String(fields?.confirmPassword || ''),
        display_name: String(fields?.displayName || ''),
        email: String(fields?.email || ''),
        phone: String(fields?.phone || ''),
      }),
    });
    let d = {};
    try {
      d = await r.json();
    } catch {
      return { ok: false, error: 'Invalid response from server.' };
    }
    if (!r.ok || !d.success) return { ok: false, error: d.error || 'Registration failed.' };
    const tok = String(d.session_token || '').trim();
    if (!tok) return { ok: false, error: 'No session returned.' };
    sessionStorage.setItem(ADMIN_SESSION_KEY, tok);
    if (fields?.remember) localStorage.setItem(ADMIN_SESSION_KEY, tok);
    else localStorage.removeItem(ADMIN_SESSION_KEY);
    const next = authedState(tok, d);
    setState(next);
    return { ok: true, role: next.role, username: next.username };
  }, []);

  const updateProfile = useCallback(
    async (displayName, email, phone) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again.' };
      const res = await authApiPost(t, {
        action: 'update_profile',
        display_name: String(displayName || ''),
        email: String(email || ''),
        phone: String(phone || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not update profile.' };
      setState((prev) =>
        prev.status === 'authed'
          ? {
              ...prev,
              displayName: String(displayName || '').trim(),
              email: String(email || '').trim(),
              phone: String(phone || '').trim(),
            }
          : prev
      );
      return { ok: true };
    },
    [state.token]
  );

  const listOrders = useCallback(async () => {
    const t = String(state.token || readStoredAdminSession() || '').trim();
    if (!t) return { ok: false, error: 'Sign in again.', orders: [] };
    try {
      const r = await fetch('/api/orders.php', {
        headers: {
          'X-Admin-Session': t,
          Authorization: `Bearer ${t}`,
        },
      });
      let d = {};
      try {
        d = await r.json();
      } catch {
        return { ok: false, error: 'Invalid response.', orders: [] };
      }
      if (!r.ok || !d.success) {
        return { ok: false, error: d.error || 'Could not load orders.', orders: [] };
      }
      return { ok: true, orders: Array.isArray(d.orders) ? d.orders : [] };
    } catch {
      return {
        ok: false,
        error: 'Could not reach the server. Run npm run php:serve alongside npm start.',
        orders: [],
      };
    }
  }, [state.token]);

  const getOrderDetail = useCallback(
    async (orderNumber) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      const num = String(orderNumber || '').trim();
      if (!t || !num) return { ok: false, error: 'Sign in again.', order: null };
      try {
        const r = await fetch(`/api/orders.php?order_number=${encodeURIComponent(num)}`, {
          headers: {
            'X-Admin-Session': t,
            Authorization: `Bearer ${t}`,
          },
        });
        let d = {};
        try {
          d = await r.json();
        } catch {
          return { ok: false, error: 'Invalid response.', order: null };
        }
        if (!r.ok || !d.success) {
          return { ok: false, error: d.error || 'Could not load order.', order: null };
        }
        return { ok: true, order: d.order || null };
      } catch {
        return {
          ok: false,
          error: 'Could not reach the server. Run npm run php:serve alongside npm start.',
          order: null,
        };
      }
    },
    [state.token]
  );

  const changePassword = useCallback(
    async (currentPassword, newPassword, confirmPassword) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again to change your password.' };
      const res = await authApiPost(t, {
        action: 'change_password',
        current_password: String(currentPassword || ''),
        new_password: String(newPassword || ''),
        confirm_password: String(confirmPassword || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not update password.' };
      return { ok: true };
    },
    [state.token]
  );

  const changeUsername = useCallback(
    async (currentPassword, newUsername) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again to change your username.' };
      const res = await authApiPost(t, {
        action: 'change_username',
        current_password: String(currentPassword || ''),
        new_username: String(newUsername || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not update username.' };
      const uname = String(res.data?.username || newUsername || '').trim();
      setState((prev) =>
        prev.status === 'authed' ? { ...prev, username: uname } : prev
      );
      return { ok: true, username: uname };
    },
    [state.token]
  );

  const listUsers = useCallback(async () => {
    const t = String(state.token || readStoredAdminSession() || '').trim();
    if (!t) return { ok: false, error: 'Sign in again.', users: [] };
    const res = await authApiPost(t, { action: 'list_users' });
    if (!res.ok) return { ok: false, error: res.error || 'Could not load users.', users: [] };
    const users = Array.isArray(res.data?.users) ? res.data.users : [];
    return {
      ok: true,
      users,
      rolesSupported: res.data?.roles_supported !== false,
    };
  }, [state.token]);

  const listCustomers = useCallback(async () => {
    const t = String(state.token || readStoredAdminSession() || '').trim();
    if (!t) return { ok: false, error: 'Sign in again.', customers: [] };
    const res = await authApiPost(t, { action: 'list_customers' });
    if (!res.ok) return { ok: false, error: res.error || 'Could not load customers.', customers: [] };
    const customers = Array.isArray(res.data?.customers) ? res.data.customers : [];
    return {
      ok: true,
      customers,
      rolesSupported: res.data?.roles_supported !== false,
    };
  }, [state.token]);

  const updateUserRole = useCallback(
    async (userId, role) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again.' };
      const res = await authApiPost(t, {
        action: 'update_user_role',
        user_id: Number(userId),
        role: String(role || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not update role.' };
      return { ok: true, role: String(res.data?.role || role) };
    },
    [state.token]
  );

  const createUser = useCallback(
    async (username, password, confirmPassword, role = 'admin') => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again.' };
      const res = await authApiPost(t, {
        action: 'create_user',
        username: String(username || ''),
        password: String(password || ''),
        confirm_password: String(confirmPassword || ''),
        role: String(role || 'admin'),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not create user.' };
      return {
        ok: true,
        username: String(res.data?.username || username),
        role: String(res.data?.role || role),
      };
    },
    [state.token]
  );

  const resetCustomerPassword = useCallback(
    async (userId, password, confirmPassword) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again.' };
      const res = await authApiPost(t, {
        action: 'admin_reset_customer_password',
        user_id: Number(userId),
        password: String(password || ''),
        confirm_password: String(confirmPassword || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not reset password.' };
      return {
        ok: true,
        username: String(res.data?.username || ''),
        message: String(res.data?.message || 'Password updated.'),
      };
    },
    [state.token]
  );

  const applySessionToken = useCallback(
    async (token, remember = true) => {
      const tok = String(token || '').trim();
      if (!tok) return { ok: false, error: 'No session token.' };
      sessionStorage.setItem(ADMIN_SESSION_KEY, tok);
      if (remember) localStorage.setItem(ADMIN_SESSION_KEY, tok);
      else localStorage.removeItem(ADMIN_SESSION_KEY);
      try {
        const r = await fetch('/api/admin_auth.php', {
          headers: {
            'X-Admin-Session': tok,
            Authorization: `Bearer ${tok}`,
          },
        });
        const d = await r.json();
        if (!d.logged_in) {
          applyGuest();
          return { ok: false, error: 'Session could not be started.' };
        }
        setState(authedState(tok, d));
        return { ok: true };
      } catch {
        return { ok: false, error: 'Session could not be started.' };
      }
    },
    [applyGuest]
  );

  const logout = useCallback(async () => {
    const t = String(state.token || readStoredAdminSession() || '').trim();
    if (t) {
      try {
        await fetch('/api/admin_auth.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Session': t,
            Authorization: `Bearer ${t}`,
          },
          body: JSON.stringify({ action: 'logout', session_token: t }),
        });
      } catch {
        /* still clear client */
      }
    }
    applyGuest();
  }, [state.token, applyGuest]);

  const value = useMemo(
    () => ({
      ...state,
      ready: state.status !== 'loading',
      isAuthed: state.status === 'authed',
      isAdmin: state.status === 'authed' && state.role === 'admin',
      isCustomer: state.status === 'authed' && state.role === 'customer',
      isStaff:
        state.status === 'authed' && (state.role === 'admin' || state.role === 'member'),
      refreshAuth,
      applySessionToken,
      login,
      register,
      logout,
      changePassword,
      changeUsername,
      updateProfile,
      listUsers,
      listCustomers,
      createUser,
      updateUserRole,
      resetCustomerPassword,
      listOrders,
      getOrderDetail,
    }),
    [
      state,
      refreshAuth,
      applySessionToken,
      login,
      register,
      logout,
      changePassword,
      changeUsername,
      updateProfile,
      listUsers,
      listCustomers,
      createUser,
      updateUserRole,
      resetCustomerPassword,
      listOrders,
      getOrderDetail,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
